/*
 *** Usage in FO: NOT OK
 *** Usage in PO: NOT OK
 *** Usage in BO: OK
 */

import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { animated, useTransition } from 'react-spring';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Simplebar } from '@hivebrite/design-system/Simplebar';
import { focusOutline } from '@hivebrite/design-system/Theme';

import Input from 'hb-react/shared/components/DesignSystem/Input';
import Loader from 'hb-react/shared/components/Loader';
import onEnterPress from 'hb-react/shared/utils/onEnterPress';

const Card = ({ card, columns, onClick, rowHeight }) => {
  const [isHovered, setHover] = useState(false);
  const [isFocus, setFocus] = useState(false);

  const transitions = useTransition(isFocus || isHovered, {
    from: { y: 100 },
    enter: { y: 0 },
    leave: { y: 100 },
  });

  const IMAGE_WIDTH = Math.floor(100 / columns) - 1;

  // Random and Search routes don't send the same response format
  const { name, links } = card.user.attributes
    ? card.user.attributes.table
    : card.user;

  const handleFocus = () => setFocus(true);
  const handleBlur = () => setFocus(false);
  const handleMouseEnter = () => setHover(true);
  const handleMouseLeave = () => setHover(false);

  return (
    <StyledCard
      data-testid="result"
      height={rowHeight}
      onClick={() => onClick(card)}
      onKeyPress={onEnterPress(() => onClick(card))}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      tabIndex={0}
      width={`${IMAGE_WIDTH}%`}
    >
      <Image image={card.urls.small} />
      {transitions(
        (styles, item) =>
          item && (
            <AuthorWrapper
              style={{
                transform: styles.y.to((y) => `translate3d(0, ${y}%, 0)`),
              }}
            >
              <Author>
                <Link href={links.html} rel="noreferrer" target="_blank">
                  {name}
                </Link>
              </Author>
            </AuthorWrapper>
          ),
      )}
    </StyledCard>
  );
};

const UnsplashImage = ({
  columns,
  error,
  rowHeight,
  defaultResults,
  label,
  hideLabel,
  onClick,
  placeholder,
  search,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [cards, setCards] = useState([]);

  useEffect(() => {
    if (defaultResults) {
      setCards(defaultResults);
    }
  }, [defaultResults]);

  const inputRef = useRef();

  const handleChangeInput = useCallback(
    ({ target: { value } }) => {
      setInputValue(value);

      initSearchDebounced(value);
    },
    [defaultResults],
  );

  const initSearchDebounced = debounce(async (value) => {
    if (value) {
      const results = await search(value);

      return setCards(results ?? []);
    }

    return setCards(defaultResults);
  }, 300);

  const renderCards = () => {
    if (!defaultResults && !error) {
      return <Loader small />;
    }

    if (error) {
      return (
        <Error>{__('Oops, something went wrong. Try again later.')}</Error>
      );
    }

    if (!cards.length) {
      return __('No results');
    }

    return (
      <CardsWrapper>
        {cards.map((card) => (
          <Card
            card={card}
            columns={columns}
            key={card.id}
            onClick={onClick}
            rowHeight={rowHeight}
          />
        ))}
      </CardsWrapper>
    );
  };

  return (
    <>
      <InputWrapper>
        <Input
          aria-label="unsplash-search"
          clearable
          disabled={error}
          hideLabel={hideLabel}
          id="unsplash-search"
          label={label}
          onChange={handleChangeInput}
          placeholder={placeholder}
          ref={inputRef}
          type="text"
          value={inputValue}
        />
      </InputWrapper>
      <Simplebar
        autoHide={false}
        style={{
          height: '500px',
        }}
      >
        {renderCards()}
      </Simplebar>
    </>
  );
};

UnsplashImage.propTypes = {
  columns: PropTypes.number,
  defaultResults: PropTypes.array,
  hideLabel: PropTypes.bool,
  label: PropTypes.string,
  onClick: PropTypes.func,
  placeholder: PropTypes.string,
  rowHeight: PropTypes.string,
  search: PropTypes.func.isRequired,
};

UnsplashImage.defaultProps = {
  columns: 4,
  hideLabel: true,
  label: 'Unsplash',
  placeholder: __('Search'),
  rowHeight: '200px',
};

export default UnsplashImage;

const Author = styled(Box).attrs(() => ({
  bg: 'fog',
  width: '100%',
}))`
  padding: ${(props) => props.theme.space[2]}px;

  > a {
    &:focus,
    &:hover {
      text-decoration: underline;
    }
  }
`;

const AuthorWrapper = styled(animated.div)`
  bottom: 0;
  left: 0;
  right: 0;
  position: absolute;
`;

const StyledCard = styled(Box).attrs(({ height, width }) => ({
  mb: 2,
  height,
  width,
}))`
  ${focusOutline}

  border-radius: 4px;
  position: relative;
  overflow: hidden;

  &:focus,
  &:hover {
    cursor: pointer;
  }
`;

const CardsWrapper = styled(Box).attrs(() => ({
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  mr: 4,
}))``;

const Error = styled(Box)`
  color: ${({ theme }) => theme.colors.fire};
`;

const Image = styled(Box).withConfig({
  shouldForwardProp: (prop) => !['image'].includes(prop),
})`
  background-image: ${({ image }) => `url(${image})`};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  height: 100%;
  width: 100%;
`;

const InputWrapper = styled(Box).attrs(() => ({
  display: 'block',
  mb: 4,
}))``;

const Link = styled.a`
  color: ${(props) => props.theme.colors.white};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
